import {Button, Dialog, Icon, Loading} from '@startlibs/components';
import {ConfirmDialog, Errors, TextInput, useConfirmDialog, useProvideErrors} from '@startlibs/form'
import {getColor, smoothScroll} from '@startlibs/utils'
import {useLazyConstant, usePrevious, useRefState, useToggle} from '@startlibs/core';
import React, {useEffect, useRef} from 'react';
import _ from 'lodash/fp'
import lighten from 'polished/lib/color/lighten'
import styled, {css} from 'styled-components'

import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd'

import {DicomBox} from './DicomBox'
import {FileParser, filterIsNonDicom, filterIsValidNonDicom, notIgnoredFiles} from '../dicom/FileParser'
import {NonCompliantBox} from './NonCompliantBox'
import {NonDicomBox} from './NonDicomBox'
import {ProcessingBox} from './ProcessingBox'
import {TransitionDiv} from '../utils/TransitionDiv'
import {ViewAllButton} from './ViewAllButton'
import {
  addDicomInstance,
  addNonCompliantInstance,
  createAttachment,
  getNonCompliantInstanceFile,
  removeDicomInstance,
  removeDicomStudy,
  removeFile,
  removeNomCompliantInstance,
  removeUnploadedInstances,
  removeUnploadedNonCompliant,
  updateDicomInstance,
  updateFile,
  updateNonCompliantInstance
} from '../utils/AttachmentsUtils';
import {getFileList} from '../dicom/FileEventToList'
import {instanceFromServerAttributes} from '../dicom/DicomInstance'
import {useDropFileEvents} from '../hooks/useDropFileEvents'
import {useGetState} from '../hooks/useAsyncState'

const ua = window.navigator.userAgent;
const isIE = /MSIE|Trident/.test(ua);

export const FileInputBoxStyle = styled.div`
  height: 9rem;
  border-radius: 5px;
  border: 1px dashed ${getColor('gray180')};
  background: ${getColor('gray240')};
  position: relative;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgba(0,0,0,0.6);
  user-select: none;
  :hover {
  //  background: rgb(230,230,230);
  }
  ${props => (props.draggingOver || props.uploading) && css`
    background: ${props => lighten(0.54, props.theme.colors.main)};
    border-color: ${getColor('main')};
  `}
  ${props => props.uploading && css`
    border-style: solid;
  `}
  input[type="file"] {
    z-index: 100;
    cursor: pointer;
    opacity: 0;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    &::-webkit-file-upload-button {
      visibility: hidden;
    }
    &::file-upload-button {
      visibility: hidden;
    }
  }
  ${Button} {
    :after {
      content: none !important;
    }
  }
`

const UploadStatusIcon = styled(Icon)`
  font-size: 28px;
  width: 3rem;
  height: 3rem;
  border-radius: 3rem;
  margin: 2rem auto;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  ${props => props.failure && css`
    background-color: ${getColor('alert')};
  `}
  ${props => props.check && css`
    background-color: ${getColor('success')};
  `}
`

export const AddButton = styled(Button).attrs({icon:'plus-circle',color:'gray240'})`
  width: 100%;
  color: rgba(0, 0, 0, 0.5);
  box-shadow: 0 0 0 1px ${getColor('gray210')};
`

const DragDropHelp = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  height: auto;
  span {
    color: rgba(0,0,0,0.3);
    font-weight: 600;
    font-size: 11px;
    margin-right: .5rem;
  }
  :after {
    flex-grow: 1;
    content: '';
    border-bottom: 1px solid rgba(0,0,0,0.1);
  }
`

const GroupWrapper = styled.div`
  position: relative;
  margin-top: 1rem;
  ${props => props.isDragging && css`
    &:before {
      content: '';
      position: absolute;
      top: -.5rem;
      right: -.5rem;
      left: -.5rem;
      bottom: -.5rem;
      background: white;
      border-radius: 6px;
      box-shadow: 0 0 4px 0 rgba(0,0,0,0.25);
      padding: .5rem .5rem 1rem;
      z-index: 1;
    }
  `}
`

const GroupContent = styled.div`
  z-index: 5;
  position: relative;
`

const GroupHeading = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  min-height: 2.5rem;
  color: ${getColor('gray150')};;
  margin: 0 0 .25rem 0;
  ${Icon} {
    font-size: 18px;
    margin-left: 0.25rem;
    vertical-align: -3px;
    &.arrow {
      font-size: 12px;
      vertical-align: -1px;
      margin: 0;
      width: .75rem;
      transition: all 0.5s ease;
      &.collapsed {
        transform: rotate(-90deg);
      }
    }
    &.arrow, &.edit {
      :hover {
        cursor: pointer;
      }
    }
  }
  .group-name-wrapper {
    flex-grow: 1;
    margin-left: .5rem;
    display: flex;
    align-items: center;
    .name {
      font-weight: 600;
    }
    :after {
      content: '';
      margin-left: 1rem;
      border-bottom: 1px solid ${getColor('gray210')};;
      flex-grow: 1;
      min-width: 150px;
    }
  }
  .records-count {
    margin-left: .5rem;
  }
  ${Button} {
    margin-left: .5rem;
  }
`

const EmptyGroupArea = styled.div`
  min-height: 4rem;
  border: 1px dashed ${getColor('gray210')};;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${getColor('gray150')};
`

const fileParser = FileParser()

const PROCESS_STATES = {
  STARTED: 1,
  FINISHED: 2,
  FOUND_FILES: 3,
  FOUND_INVALID_FILES: 4,
  FOUND_REPEATED_DICOM: 5,
}

const synchonousUpload = window.safari

const getItemId = (item) => {
  if (item.uid) {
    return "uid:" + item.uid
  } else if (item.studyUID) {
    return "studyuid:" + (item.studyUID)
  } else if (item.key) {
    return "key:" + item.key
  } else {
    return 'noncompliant'
  }
}

export const FileinputBox = React.forwardRef
(({
    jwt,
    appJwt,
    allowDownload = true,
    withViewAllButton,
    persistRecord,
    removePersistedRecord,
    disabled,
    medicalRecords,
    setNotification,
    parallelFiles = 2,
    isApp,
    withoutDelete,
    allowReorder,
    requestId
  }, ref) => {

  const isDragAndDropAvailable = useLazyConstant(() => {
    var div = document.createElement('div')
    return (('draggable' in div) || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window
  })

  const [unprocessedFiles, setUnprocessedFiles, getUnprocessedFiles] = useGetState([])

  const [dicomAttachments, setDicomAttachments, getDicomAttachments] = useGetState(medicalRecords.dicomStudies?.map(_.update('instances', (instances) => instances || [])) || [])
  const [nonCompliantDicomAttachments, setNonCompliantDicomAttachments, getNonCompliantDicomAttachments] = useGetState(medicalRecords.nonCompliantDicom || [])
  const [nonDicomAttachments, setNonDicomAttachments, getNonDicomAttachments] = useGetState(medicalRecords.nonDicomFiles || [])
  const [attachmentGroups, setAttachmentGroups, getAttachmentGroups] = useGetState(medicalRecords.groups?.list || [])
  const isCollapsedRef = useRefState([])

  const uploadingFiles = useRefState([])
  const failedFiles = useRefState([])
  const pendingFiles = useRefState([],(prev,next) => {
    const newFiles = _.difference(next,prev)
    if (newFiles.length) {
      setAttachmentGroups((groups) => {
        const inGroups = _.flatten(groups.map(_.get('items')))
        const notInGroups = newFiles.filter(attachment => inGroups.indexOf(getItemId(attachment)) < 0)
        if (notInGroups.length) {
          return _.update([0, 'items'], _.union(_, notInGroups.map(getItemId)),groups)
        } else {
          return groups
        }
      })
    }
  })

  const processingFile = useRefState()

  const existingDicomInstances = useRefState([])

  const processingState = useRefState(PROCESS_STATES.FINISHED)
  const invalidFiles = useRefState([])
  const [ErrorProvider, errors, {setError}] = useProvideErrors()

  const cancelProcessDialog = useToggle()
  const processingTimeout = useToggle()

  const confirmCancelUploads = useToggle()



  const [isDraggingOver, dragBoxRef] = useDropFileEvents((e) => {
    processingTimeout.open()
    setFile(e)
  })

  const resolveConfirmRef = useRefState()
  React.useImperativeHandle(ref, () => ({
    confirm: () => {
      if (uploadingFiles.get().length || pendingFiles.get().length || failedFiles.get().length) {
        return new Promise((res) => {
          resolveConfirmRef.set(() => () => {
            confirmCancelUploads.close()
            res()
          })
          confirmCancelUploads.open()
        })
      } else {
        return Promise.resolve()
      }
    }
  }), [])


  const setFile = (e) => {
    e.stopPropagation()
    e.preventDefault()
    getFileList(e)
      .then((newFiles) => {
        const filesNotIgnored = newFiles.filter(notIgnoredFiles)
        const nonDicom = filesNotIgnored.filter(filterIsNonDicom).filter(filterIsValidNonDicom).map(createAttachment)
        const possibleDicom = filesNotIgnored.filter((file) => !filterIsNonDicom(file) || !filterIsValidNonDicom(file))
        processingTimeout.closeAfter(2000)
        setUnprocessedFiles(files => _.orderBy(['size'], ['asc'], files.concat(possibleDicom)))
        setNonDicomAttachments(files => _.orderBy(['size'], ['asc'], _.concat(nonDicom, files)))
        pendingFiles.set(_.concat(nonDicom))
      })
      .catch(console.log)
    isDraggingOver.close()
    if (e.target && e.target.value) {
      e.target.value = ""
    }
  }

  const addNonDicomAttachment = (file) => {
    const attachment = createAttachment(file)
    setNonDicomAttachments(_.concat([attachment]))
    pendingFiles.set(_.concat([attachment]))
  }

  const processFile = (nextFile) => {
    const updateProcessState = (newState) => (currentState) => (currentState === PROCESS_STATES.STARTED || currentState === PROCESS_STATES.FOUND_REPEATED_DICOM) ? newState : currentState
    return fileParser(nextFile, uploadingFiles.get().length < parallelFiles)
      .then(({file, dicomInstance, byteArray}) => {
        if (dicomInstance) {
          if (dicomInstance.isCompliant) {
            setDicomAttachments(addDicomInstance(dicomInstance, file, byteArray))
          } else {
            if (!getNonCompliantDicomAttachments().find(({instanceUID}) => instanceUID === dicomInstance.instanceUID)) {
              setNonCompliantDicomAttachments(addNonCompliantInstance(dicomInstance, file, byteArray))
            } else {
              processingState.set(updateProcessState(PROCESS_STATES.FOUND_REPEATED_DICOM))
              return;
            }
          }
          if (existingDicomInstances.get().indexOf(dicomInstance.instanceUID) < 0) {
            if (byteArray && uploadingFiles.get().length < parallelFiles) {
              uploadingFiles.set(_.concat([{...dicomInstance, sourceFile: file}]))
            } else {
              pendingFiles.set(_.concat([{...dicomInstance, sourceFile: file}]))
            }
            existingDicomInstances.set(_.concat(dicomInstance.instanceUID))
            processingState.set(updateProcessState(PROCESS_STATES.FOUND_FILES))
          } else {
            processingState.set(updateProcessState(PROCESS_STATES.FOUND_REPEATED_DICOM))
          }
        } else {
          processingState.set(updateProcessState(PROCESS_STATES.FOUND_FILES))
          addNonDicomAttachment(file)
        }
      })
      .catch(e => {
        // if (e && e.nonReadable) {
        //   return {retry:true}
        if (e && e.invalidExtension) {
          invalidFiles.set(_.concat(nextFile))
          processingState.set(PROCESS_STATES.FOUND_INVALID_FILES)
        } else if (e && e.nonCompliant) {
          const attachment = createAttachment(nextFile)
          setNonCompliantDicomAttachments(addNonCompliantInstance(attachment, nextFile))
          pendingFiles.set(_.concat([{...attachment, nonCompliant: true, sourceFile: nextFile}]))
        } else if (!e || !e.empty) {
          processingState.set(updateProcessState(PROCESS_STATES.FOUND_FILES))
          addNonDicomAttachment(nextFile)
        } else {
          processingState.set(PROCESS_STATES.FOUND_INVALID_FILES)
        }
      })
  }

  const startNextUpload = () => {
    if (uploadingFiles.get().length >= parallelFiles || !pendingFiles.get().length) {
      if (getUnprocessedFiles().length) {
        processAndUpload()
      }
      return
    }
    const attachment = _.head(pendingFiles.get())
    pendingFiles.set(_.without([attachment]))
    uploadingFiles.set(_.concat(attachment))
    const updater = _.set('fileToUpload', attachment.sourceFile)
    if (attachment.isCompliant) {
      setDicomAttachments(updateDicomInstance(attachment, updater))
    } else if (attachment.isCompliant === false || attachment.nonCompliant) {
      setNonCompliantDicomAttachments(updateNonCompliantInstance(attachment, updater))
    } else {
      setNonDicomAttachments(updateFile(attachment, updater))
    }
    processAndUpload()
  }

  const processAndUpload = () => {
    if (processingFile.get() || cancelProcessDialog.get()) {
      return
    }
    if (synchonousUpload) {
      if (uploadingFiles.get().length) {
        return
      }
    }
    if (getUnprocessedFiles().length) {
      const nextFile = _.head(getUnprocessedFiles())
      processingFile.set(nextFile)
      processFile(nextFile).finally(() => {
        processingFile.set(false)
        setUnprocessedFiles(_.without([nextFile]))
        startNextUpload()
      })
    }
    startNextUpload()
  }


  useEffect(() => {
    if (unprocessedFiles.length === 0) {
      if (processingState.get() === PROCESS_STATES.FOUND_REPEATED_DICOM) {
        setNotification("DICOM files selected to upload are already attached to this request.")
      }
      if (invalidFiles.get().length) {
        if (invalidFiles.get().length === 1) {
          setError("fileType", invalidFiles.get()[0].name + " could not be uploaded. File type not supported.")
        } else {
          setError("fileType", invalidFiles.get().length + " files could not be uploaded due to file types not being supported.")
        }
        invalidFiles.set([])
      }
      processingState.set(PROCESS_STATES.STARTED)
    }
    if (unprocessedFiles.length && processingState.get() === PROCESS_STATES.FINISHED) {
      processingState.set(PROCESS_STATES.STARTED)
    }
    processAndUpload()
  }, [unprocessedFiles, cancelProcessDialog.isOpen])


  const uploadsProcessing = uploadingFiles.get().length
  const onlyFailedUploads = !uploadsProcessing && failedFiles.get().length

  const onDragEnd = ({source, destination, ...rest}) => {
    if (!destination) {
      return
    }
    const isCollapsed = isCollapsedRef.get().indexOf(source.id) >= 0
    const add = (index, item) => list => isCollapsed ? list.concat(item) : [...list.slice(0, index), item, ...list.slice(index)]
    const remove = (index) => (list) => [...list.slice(0, index), ...list.slice(index + 1)]

    setAttachmentGroups(groups => {
      if (source.droppableId === "board") {
        const removed = remove(source.index)(groups)
        return add(destination.index, groups[source.index])(removed)
      } else {
        const clearNulls = _.filter(item => flatAttachments.indexOf(item) >= 0)
        const item = clearNulls(groups.find(({id}) => id === source.droppableId).items)[source.index]
        // const item = groups.find(({id}) => id === source.droppableId).items[source.index]
        return groups.map(group => {
          return _.flow(
            _.update('items',clearNulls),
            group.id === source.droppableId ? _.update('items', remove(source.index)) : _.identity,
            group.id === destination.droppableId ? _.update('items', add(destination.index, item)) : _.identity
          )(group)
        })
      }
    })
  }


  useEffect(() => {
    const ungrouped = _.orderBy([({ uploadDate }) => uploadDate || 0],['desc'],[...dicomAttachments, ...nonDicomAttachments].concat(nonCompliantDicomAttachments.length ? {nonCompliant: true} : []))
      .map(getItemId)
    const inGroup = _.flatten(attachmentGroups.map(_.get('items')))

    const newUngroupeds = _.difference(ungrouped, inGroup)
    if (newUngroupeds.length) {
      setAttachmentGroups(groups => {
        if (groups.find(g => g.id === "ungrouped")) {
          return groups.map(g => g.id === 'ungrouped' ? {...g, items: newUngroupeds.concat(g.items)} : g)
        } else {
          return [{id: 'ungrouped', items: newUngroupeds}].concat(groups)
        }
      })
    } else if (!attachmentGroups.length) {
      return setAttachmentGroups([{id: 'ungrouped', items: []}])
    }
  }, [])


  const addGroup = () => {
    setAttachmentGroups(_.concat(_, {name: 'New group', id: Date.now().toString(36), items: [], transient: true}))
  }


  const uploaderUtils = useLazyConstant(() => ({
    setNonCompliantDicomAttachments,
    setDicomAttachments,
    getDicomAttachments,
    getNonCompliantDicomAttachments,
    setNonDicomAttachments,
    getNonDicomAttachments,
    persistRecord,
    removePersistedRecord,
    existingDicomInstances,
    pendingFiles,
    failedFiles,
    isCollapsedRef,
    startNextUpload,
    setAttachmentGroups,
    uploadingFiles
  }))

  const flatAttachments = [...dicomAttachments.map(getItemId),...nonDicomAttachments.map(getItemId),...(nonCompliantDicomAttachments.length ? [getItemId(nonCompliantDicomAttachments)] : [])]
  const prevFlatAttachment = usePrevious(flatAttachments)

  useEffect(() => {
    if (flatAttachments.length < prevFlatAttachment?.length) {
      setAttachmentGroups(_.map(_.update('items',_.filter(id => flatAttachments.indexOf(id) >= 0))))
    }
  },[flatAttachments.join(";")])


  const previousAttachmentGroups = usePrevious(attachmentGroups)
  useEffect(() => {
    if (allowReorder && previousAttachmentGroups && JSON.stringify(attachmentGroups) !== JSON.stringify(previousAttachmentGroups)) {
      const retry = () => persistRecord({groups:getAttachmentGroups()})
        .catch(() =>
          setNotification({type:"alert", timeout: 0,msg:(close) => <span>Could not save group <a onClick={() => { close(); retry()}}>Retry</a></span>})
        )
      retry()
    }
  },[attachmentGroups])

  return <>
    {
      !disabled &&
      <FileInputBoxStyle
        draggingOver={isDraggingOver.isOpen}
        ref={dragBoxRef}
      >
        {isDragAndDropAvailable
          ? <div>
            {isDraggingOver.isOpen
              ?
              <>
                <b>Drop files heres to upload them</b>
              </>
              :
              <>
                <Button.a highlight small css="margin-bottom:0.5rem;"><input
                  type="file"
                  multiple
                  onChange={setFile}
                />Select files</Button.a>
                {!isIE && <Button.a highlight small css="margin-bottom:0.5rem;"><input
                  webkitdirectory="true" type="file" multiple
                  onChange={setFile}
                />Select folders or
                  disks</Button.a>}
                <div>Or drag and drop them into this box</div>
              </>
            }
          </div>
          :
          <div>
            <input type="file" multiple onChange={setFile}/>
            <b>Click here to select file(s) to upload</b>
          </div>
        }
      </FileInputBoxStyle>
    }
    <ErrorProvider value={errors}>
      <Errors/>
    </ErrorProvider>
    {
      (unprocessedFiles.length > 0 || processingTimeout.isOpen) &&
      <ProcessingBox
        cancelProcessDialog={cancelProcessDialog} unprocessedFiles={unprocessedFiles}
        setUnprocessedFiles={setUnprocessedFiles}
      />
    }
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId="board"
        type="GROUP"
      >
        {(provided) => (
          <div css="margin: 1rem 0;" ref={provided.innerRef} {...provided.droppableProps}>
            {attachmentGroups.map((group, index) => (
              <AttachmentGroup
                key={group.id}
                groupsLength={attachmentGroups.length}
                flatAttachments={flatAttachments}
                index={index}
                group={group}
                allowReorder={allowReorder && !disabled}
                nonCompliantDicomAttachments={nonCompliantDicomAttachments}
                dicomAttachments={dicomAttachments}
                nonDicomAttachments={nonDicomAttachments}
                uploaderUtils={uploaderUtils}
                jwt={jwt}
                appJwt={appJwt}
                disabled={disabled}
                withoutDelete={withoutDelete}
                isApp={isApp}
                unprocessedFiles={unprocessedFiles}
                allowDownload={allowDownload}
                canMoveGroup={attachmentGroups.length > 2}
                requestId={requestId}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
    {allowReorder && !disabled && (flatAttachments.length>0 || attachmentGroups.length > 1) && <AddButton onClick={addGroup}>Add a new record group</AddButton>}
    {
      confirmCancelUploads.isOpen &&
      <Dialog
        title={
          uploadsProcessing ? 'There are still uploads remaining'
            : (onlyFailedUploads ? 'There are failed uploads to check'
            : 'Uploads are now finished')
        }
        closeDialog={confirmCancelUploads.close}
        footer={<>
          <Button
            onClick={() => {
              confirmCancelUploads.close();
              smoothScroll(dragBoxRef.current)
            }}
          >Check uploads</Button>
          {(!uploadsProcessing && !onlyFailedUploads) &&
          <Button highlight onClick={resolveConfirmRef.get()}>Proceed</Button>}
        </>}
      >
        {
          onlyFailedUploads
            ? <>
              <p>You are about to leave this page but there are some failed uploads to check.</p>
              <p>Please check your failed uploads before proceeding.</p>
              <UploadStatusIcon icon="failure" failure/>
            </>
            : uploadsProcessing
            ? <>
              <p>You are about to leave this page but there are still uploads to be handled.</p>
              <p>Please wait for the uploads to finish or review them before leaving.</p>
              <Loading css="margin: 2rem auto;"/>
            </>
            : <>
              <p>All your uploads have now finished. It is safe to leave this page.</p>
              <UploadStatusIcon icon="check" check/>
            </>
        }
      </Dialog>
    }
    {withViewAllButton && <div><ViewAllButton dicomAttachments={dicomAttachments} jwt={appJwt}/></div>}
  </>
})


const AttachmentGroup =
  ({
     group,
     index,
     groupsLength,
     jwt,
     appJwt,
     disabled,
     withoutDelete,
     isApp,
     unprocessedFiles,
     allowDownload,
     uploaderUtils,
     nonCompliantDicomAttachments,
     dicomAttachments,
     nonDicomAttachments,
     canMoveGroup,
     flatAttachments,
    allowReorder,
    requestId
   }) => {

    const collapsed = useToggle(false, (v) => uploaderUtils.isCollapsedRef.set(v ? _.union([group.id]) : _.difference(_, [group.id])))

    const availableItems = group.items.filter((item) => flatAttachments.indexOf(item) >= 0)

    const updateItemKey = (attachment,newAttachment) => _.map(_.update('items', items => {
        const index = items.indexOf(getItemId(attachment))
        if (index >= 0) {
          return [...items.slice(0, index), getItemId(newAttachment), ...items.slice(index + 1)]
        } else {
          return items
        }
      })
    )

    const updateItemNonComp = (attachment,newAttachment) => 
      _.map(_.update('items', items => {
        const index = items.findIndex((item) => item === 'studyuid:'+attachment.studyUID)
        if (index >= 0) {
          items[index] = 'noncompliant'
          return items
        } else {
          return items
        }
      })
    )
    
    const finishUpload = (instance) => response => {
      const uploadDate = Date.now()
      const updater = _.flow(
        _.unset(['sourceFile']),
        _.unset(['fileToUpload']),
        _.set(['uploadDate'],uploadDate)
      )
      if (instance.isCompliant || instance.nonCompliant) {
        if (response.dicomAttributes) { // uuid -> SERVER NON COMPLIANT
          const updatedInstance = instanceFromServerAttributes(response.dicomAttributes, instance)
          const updater = () => updatedInstance
          if (instance.nonCompliant) {
            uploaderUtils.setNonCompliantDicomAttachments(removeNomCompliantInstance(instance))
            uploaderUtils.setDicomAttachments(_.flow(
              addDicomInstance(updatedInstance),
            ))
          }
          const study = uploaderUtils.getDicomAttachments().find(({studyUID}) => studyUID === instance.studyUID)

          if (study && (study.isNew || study.seriesUIDs.indexOf(instance.seriesUID) < 0)) {
            uploaderUtils.persistRecord(
              {
                dicomStudy:
                  _.flow(
                    _.set('uploadDate',uploadDate),
                    _.update('seriesUIDs', _.union(instance.seriesUID ? [instance.seriesUID] : [])),
                    _.update('modalities', _.union(instance.modality ? [instance.modality] : []))
                  )(study)
              }
            ).then(() => uploaderUtils.setDicomAttachments((prevAttachments) => _.flow(
              updateDicomInstance(instance, updater),
              _.unset([prevAttachments.findIndex(({studyUID}) => studyUID === instance.studyUID), 'isNew']),
              _.set([prevAttachments.findIndex(({studyUID}) => studyUID === instance.studyUID),'uploadDate'],Date.now())
              )(prevAttachments)
            ))
          } else {
            uploaderUtils.setDicomAttachments(updateDicomInstance(instance, updater))
          }
        } else {
          const attachmentUpdater = _.flow(
            updater,
            _.set('uid', response.uuid)
          )
          if (instance.isCompliant) {
            uploaderUtils.setDicomAttachments(removeDicomInstance(instance))
            uploaderUtils.setNonCompliantDicomAttachments(_.flow(
              addNonCompliantInstance(updater(instance), instance.sourceFile),
            ))
          }
          const attachment = instance.isCompliant ? getNonCompliantInstanceFile(instance, instance.sourceFile) : uploaderUtils.getNonCompliantDicomAttachments().find(a => a.sourceFile === instance.sourceFile)
          uploaderUtils.persistRecord({nonCompliant: attachmentUpdater(attachment)})
            .then(() => {
              uploaderUtils.setNonCompliantDicomAttachments(updateNonCompliantInstance(instance, attachmentUpdater))
              uploaderUtils.setAttachmentGroups(updateItemNonComp(attachment,{uid:response.uuid}))
            })
        }
      } else {
        const attachmentUpdater = _.flow(
          updater,
          _.set('uid', response.uuid)
        )
        const attachment = uploaderUtils.getNonDicomAttachments().find(a => a.sourceFile === instance.sourceFile)
        uploaderUtils.persistRecord({nonDicomFile: attachmentUpdater(attachment)})
          .then(() => {
            uploaderUtils.setNonDicomAttachments(updateFile(instance, attachmentUpdater))
            uploaderUtils.setAttachmentGroups(updateItemKey(attachment,{uid:response.uuid}))
          })
      }
      uploaderUtils.uploadingFiles.set(_.differenceBy(_.property('sourceFile'), _, [instance]))
      uploaderUtils.startNextUpload()
    }

    const onFailure = (instance) => (error) => {
      const update = _.flow(
        _.unset('fileToUpload'),
        _.set('failed', error)
      )
      if (instance.instanceUID || instance.nonCompliant) {
        if (instance.isCompliant) {
          uploaderUtils.setDicomAttachments(updateDicomInstance(instance, update))
        } else {
          uploaderUtils.setNonCompliantDicomAttachments(updateNonCompliantInstance(instance, update))
        }
      } else {
        uploaderUtils.setNonDicomAttachments(updateFile(instance, update))
      }
      uploaderUtils.uploadingFiles.set(_.differenceBy(_.property('sourceFile'), _, [instance]))
      uploaderUtils.failedFiles.set(_.concat([instance.sourceFile]))
      uploaderUtils.startNextUpload()
    }

    const retryToQueue = (instance) => () => {
      const update = _.unset('failed')
      if (instance.instanceUID || instance.nonCompliant) {
        if (instance.isCompliant) {
          uploaderUtils.setDicomAttachments(updateDicomInstance(instance, update))
        } else {
          uploaderUtils.setNonCompliantDicomAttachments(updateNonCompliantInstance(instance, update))
        }
      } else {
        uploaderUtils.setNonDicomAttachments(updateFile(instance, update))
      }
      uploaderUtils.pendingFiles.set(_.concat([instance]))
      uploaderUtils.failedFiles.set(_.without([instance.sourceFile]))
      uploaderUtils.startNextUpload()
    }

    const onCancel = (instance) => () => {
      if (instance.instanceUID || instance.nonCompliant) {
        if (instance.isCompliant) {
          uploaderUtils.setDicomAttachments(removeDicomInstance(instance))
        } else {
          uploaderUtils.setNonCompliantDicomAttachments(removeNomCompliantInstance(instance))
        }
        uploaderUtils.existingDicomInstances.set(_.difference(_, [instance.instanceUID]))
      } else {
        uploaderUtils.setNonDicomAttachments(removeFile(instance))
      }
      uploaderUtils.uploadingFiles.set(_.differenceBy(_.property('sourceFile'), _, [instance]))
      uploaderUtils.pendingFiles.set(_.differenceBy(_.property('sourceFile'), _, [instance]))
      uploaderUtils.failedFiles.set(_.without([instance.sourceFile]))
      uploaderUtils.startNextUpload()
    }

    const removeStudyFromQueue = (study) => {
      const instanceUIDsToRemove = study.instances.filter(({sourceFile}) => sourceFile).map(({instanceUID}) => instanceUID)
      const sourceFilesToRemove = study.instances.map(({sourceFile}) => sourceFile).filter(_.identity)
      uploaderUtils.existingDicomInstances.set(_.difference(_, instanceUIDsToRemove))
      uploaderUtils.pendingFiles.set(_.filter(instance => instanceUIDsToRemove.indexOf(instance.instanceUID) < 0))
      uploaderUtils.failedFiles.set(_.filter(sourceFile => sourceFilesToRemove.indexOf(sourceFile) < 0))
      uploaderUtils.setDicomAttachments(removeUnploadedInstances(study))
    }

    const removeNonCompliantFromQueue = () => {
      const filesToRemove = nonCompliantDicomAttachments.filter(({sourceFile}) => sourceFile)
      const instanceUIDsToRemove = nonCompliantDicomAttachments.map(({instanceUID}) => instanceUID)
      const sourceFilesToRemove = filesToRemove.map(({sourceFile}) => sourceFile)
      uploaderUtils.existingDicomInstances.set(_.difference(_, instanceUIDsToRemove))
      uploaderUtils.pendingFiles.set(_.filter(instance => sourceFilesToRemove.indexOf(instance.sourceFile) < 0))
      uploaderUtils.setNonCompliantDicomAttachments(removeUnploadedNonCompliant)
      uploaderUtils.failedFiles.set(_.filter(sourceFile => sourceFilesToRemove.indexOf(sourceFile) < 0))
    }

    const removeStudy = (study) => {
      uploaderUtils.existingDicomInstances.set(_.difference(_, study.instances.map(_.property('instanceUID'))))
      return uploaderUtils.removePersistedRecord({studyUID: study.studyUID})
        .then(() =>
          uploaderUtils.setDicomAttachments(removeDicomStudy(study))
        )
    }

    const removeNonDicomFile = (attachment) =>
      uploaderUtils.removePersistedRecord({fileUID: attachment.uid})
        .then(() =>
          uploaderUtils.setNonDicomAttachments(removeFile(attachment))
        )

    const persistAttachmentInfo = (attachment) => (cb) =>
      uploaderUtils.setNonDicomAttachments((attachments) => {
        const prevAttachment = attachments.find(a => a.sourceFile === attachment.sourceFile || a.uid === attachment.uid)
        const nextAttachment = updateFile(attachment, cb)(attachments)
        if (attachment.uid) {
          uploaderUtils.persistRecord({nonDicomFile: cb(attachment)})
            .catch(() => uploaderUtils.setNonDicomAttachments(updateFile(attachment, (a) => a === nextAttachment ? prevAttachment : a)(attachments)))
        }
        return updateFile(attachment, cb)(attachments)
      })

    const removeNonCompliant = (nonCompliant) =>
      uploaderUtils.removePersistedRecord({nonCompliant})
        .then(() =>
          uploaderUtils.setNonCompliantDicomAttachments(nonCompliant)
        )

    const removeNonCompliantFile = (nonCompliantFile) =>
      uploaderUtils.removePersistedRecord({nonCompliantFile})
        .then(() =>
          uploaderUtils.setNonCompliantDicomAttachments(removeNomCompliantInstance(nonCompliantFile))
        )


    const renderDraggableItem = (id, dragProvided, isDragging) => {
      if (id.indexOf("uid:") === 0 || id.indexOf("key:") === 0) {
        const attachment = nonDicomAttachments.find(item => getItemId(item) === id)
        if (!attachment) return null
        return <NonDicomBox
          jwt={jwt}
          appJwt={appJwt}
          disabled={disabled}
          withoutDelete={withoutDelete}
          key={attachment.uid || attachment.key}
          onFinishUpload={finishUpload(attachment)}
          onCancel={onCancel(attachment)}
          onFailure={onFailure(attachment)}
          retry={retryToQueue(attachment)}
          setAttachment={persistAttachmentInfo(attachment)}
          removeFile={() => removeNonDicomFile(attachment)}
          attachment={attachment}
          isApp={isApp}
          dragProvided={dragProvided}
          requestId={requestId}
        />
      } else if (id.indexOf("studyuid:") === 0) {
        const study = dicomAttachments.find(item => getItemId(item) === id)
        if (!study) return null
        return <DicomBox
          jwt={jwt}
          appJwt={appJwt}
          disabled={disabled}
          withoutDelete={withoutDelete}
          key={study.studyUID}
          noCancel={unprocessedFiles.length > 0}
          hasUnprocessedFiles={unprocessedFiles.length > 0}
          setSeriesList={uploaderUtils.setDicomAttachments}
          study={study}
          allowDownload={allowDownload}
          onFinishUpload={finishUpload}
          onCancel={onCancel}
          onFailure={onFailure}
          removeStudy={removeStudy}
          removeStudyFromQueue={removeStudyFromQueue}
          retry={retryToQueue}
          isApp={isApp}
          dragProvided={dragProvided}
        />
      } else if (id === "noncompliant") {
        if (!nonCompliantDicomAttachments.length) return null
        return <NonCompliantBox
          jwt={jwt}
          appJwt={appJwt}
          disabled={disabled}
          withoutDelete={withoutDelete}
          removeAttachment={removeNonCompliant}
          removeNonCompliantFile={removeNonCompliantFile}
          attachments={nonCompliantDicomAttachments}
          onFinishUpload={finishUpload}
          onCancel={onCancel}
          onFailure={onFailure}
          removeFromQueue={removeNonCompliantFromQueue}
          retry={retryToQueue}
          isApp={isApp}
          dragProvided={dragProvided}
        />
      }
    }

    const droppable = (snapshot,moveHandle) => <Droppable
      droppableId={group.id}
      isCombineEnabled={collapsed.isOpen}
    >
      {(dropProvided, dropSnapshot) => <GroupContent>
        <GroupHeader
          flatAttachments={flatAttachments}
          snapshot={snapshot}
          collapsed={collapsed}
          group={group}
          allowReorder={allowReorder}
          moveHandle={moveHandle}
          setGroups={uploaderUtils.setAttachmentGroups}
        />
        <div
          css="position:relative;"
          {...dropProvided.droppableProps}
          ref={dropProvided.innerRef}
        >
          <TransitionDiv>{
            !collapsed.isOpen &&
            availableItems.map((item, index) =>
              <Draggable
                isDragDisabled={!allowReorder || (flatAttachments.length < 2 && groupsLength === 1)}
                key={item} draggableId={item} index={index}
              >
                {(dragProvided, dragSnapshot) => renderDraggableItem(item, dragProvided, dragSnapshot.isDragging)}
              </Draggable>
            )
          }</TransitionDiv>
          <div>
          <TransitionDiv>{
            group.id !== 'ungrouped' && !group.items.length && allowReorder && !collapsed.isOpen &&
            <EmptyGroupArea>This group is empty. Drag records here to add them to this group.</EmptyGroupArea>
          }</TransitionDiv>
          </div>
          {dropProvided.placeholder}
        </div>
      </GroupContent>}
    </Droppable>


    if (!allowReorder && !availableItems.length) {
      return null
    }

    return <div
      key={group.id}
      index={index}
      group={group}
    >
      {
        group.id === 'ungrouped'
          ? droppable()
          : <Draggable draggableId={group.id} index={index} isDragDisabled={!canMoveGroup || !allowReorder}>
            {(provided, snapshot) =>
              <GroupWrapper ref={provided.innerRef} {...provided.draggableProps} isDragging={snapshot.isDragging}>
                {droppable(snapshot,canMoveGroup && allowReorder && <div {...provided.dragHandleProps}><Icon icon="move" /></div>)}
              </GroupWrapper>
            }
          </Draggable>
      }
    </div>

  }

const GroupHeader = ({snapshot, collapsed, group, setGroups, moveHandle, flatAttachments, allowReorder}) => {

  const editingName = useToggle(group.transient && "New group")

  const editingIsOpen = _.isString(editingName.isOpen)
  const ungroupRef = useRef()

  const confirmUngroup = useConfirmDialog(<ConfirmDialog
    title={`Ungroup records`}
    action={() => setGroups(_.flow(
      _.without([group]),
      _.update([0, 'items'], _.concat(group.items))
    ))}
    confirm={<Button alert>Proceed</Button>}
    cancelLabel="Cancel"
    css="max-width: 42rem;"
  >
    <p>
      Any records in this group will be separated and moved to the top of the records list.
    </p>
    <p>Would you like to proceed?</p>
  </ConfirmDialog>)

  const ungroup = () => {
    if (!group.items.length) {
      setGroups(_.without([group]))
    } else {
      editingName.close()
      confirmUngroup()
    }
  }

  const confirmName = (e) => {
    const relatedTarget = e.relatedTarget
    setTimeout(() => {
      setGroups(_.map((g) => g.id === group.id ? _.unset('transient', {...g, name: editingName.isOpen}) : g))
      if (relatedTarget !== ungroupRef.current) {
        editingName.close()
      }
    },100)
  }

  const tryConfirmName = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      confirmName(e)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      editingName.close()
    }
  }

  if (group.id === 'ungrouped') {
    if (flatAttachments.length > 1 && allowReorder) {
      return <DragDropHelp><span>Drag and drop records to reorder</span></DragDropHelp>
    } else {
      return null
    }
  }


  return <GroupHeading isDragging={snapshot?.isDragging}>
      <Icon className={"arrow" + (collapsed.isOpen ? " collapsed" : "")} icon="arrow-down" onClick={collapsed.toggle}/>
      <div className="group-name-wrapper">
        {editingIsOpen
          ? <TextInput
            onFocus={e => e.target.select()}
            raw value={editingName.isOpen} setValue={_.unary(editingName.openWith)}
            autoFocus
            onKeyUp={tryConfirmName}
            onKeyPress={e => e.key === "Enter" && e.preventDefault()}
            onBlur={confirmName}
            maxLength={255}
            css="max-width: 150px; height: 2rem; font-size: 13px;"
          />
        : <span className="name" onClick={allowReorder ? editingName.willOpenWith(group.name) : undefined}>{group.name} {allowReorder && <Icon icon="edit-box" className="edit" onClick={editingName.willOpenWith(group.name)}/>}</span>
        }
      </div>
      {collapsed.isOpen && <span className="records-count">{group.items.length} record{(group.items.length > 1 || group.items.length === 0) ? 's' : ''}</span>}
      {
        editingIsOpen && <Button small ref={ungroupRef} onClick={ungroup}>Ungroup</Button>
      }
      {moveHandle}
  </GroupHeading>

}
